import { makeObservable, observable, action, computed } from "mobx";
import type {
  TimestampedAssemblyAITurnMessage,
  TranscriptSnapshot,
} from "@/domains/dexie-db/audio-capture/types";
import type { TranscriptionChannelManager } from "@/store/audio-capture/transcription/interfaces";

// Lightweight manager compatible with the subset of methods the store uses.
export class DesktopTranscriptionManager implements TranscriptionChannelManager {
  readonly channel: number; // 0 = Me, 1 = Other Speakers

  transcriptMap: Map<number, TimestampedAssemblyAITurnMessage> = new Map();
  transcriptSnapshots: TranscriptSnapshot[] = [];
  private turnCounter = 0;
  private realtimeActive = false;

  constructor(channel: number) {
    this.channel = channel;
    makeObservable<this, "turnCounter" | "realtimeActive">(this, {
      channel: false,
      transcriptMap: observable,
      transcriptSnapshots: observable,
      initializeTranscription: action,
      restoreTranscripts: action,
      reset: action,
      ingestTurn: action,
      startTranscription: action,
      pauseTranscription: action,
      resumeTranscription: action,
      stopTranscription: action,
      cleanupWebSocketAndAudio: false,
      flushAudioBuffer: false,
      turnCounter: false,
      realtimeActive: observable,
      allTranscriptedParts: computed,
      transcript: computed,
      isTranscriptionPaused: computed,
    });
  }

  initializeTranscription({ clearTranscript = false }: { clearTranscript?: boolean } = {}) {
    if (clearTranscript) {
      this.transcriptMap.clear();
      this.transcriptSnapshots = [];
      this.turnCounter = 0;
    }
  }

  restoreTranscripts({
    transcriptSnapshots,
    transcripts,
  }: {
    transcriptSnapshots?: TranscriptSnapshot[];
    transcripts?: TimestampedAssemblyAITurnMessage[];
  }) {
    this.transcriptSnapshots = transcriptSnapshots || [];
    if (transcripts?.length) {
      transcripts.forEach(m => {
        const turn_order = m.turn_order ?? ++this.turnCounter;
        this.transcriptMap.set(turn_order, { ...m, turn_order });
      });
    }
  }

  startTranscription() {
    this.realtimeActive = true;
  }
  pauseTranscription() {
    this.realtimeActive = false;
  }
  resumeTranscription() {
    this.realtimeActive = true;
  }
  stopTranscription() {
    this.realtimeActive = false;
  }
  cleanupWebSocketAndAudio() {}
  flushAudioBuffer() {}

  reset({ clearTranscript }: { clearTranscript?: boolean } = {}) {
    this.realtimeActive = false;
    if (clearTranscript) {
      this.transcriptMap.clear();
      this.transcriptSnapshots = [];
      this.turnCounter = 0;
    }
  }

  ingestTurn(
    message: Omit<TimestampedAssemblyAITurnMessage, "turn_order"> & { turn_order?: number }
  ) {
    let key: number;
    if (typeof message.turn_order === "number" && Number.isFinite(message.turn_order)) {
      key = message.turn_order;
      if (key > this.turnCounter) {
        this.turnCounter = key;
      }
    } else {
      key = ++this.turnCounter;
    }
    this.transcriptMap.set(key, {
      ...message,
      turn_order: key,
    } as TimestampedAssemblyAITurnMessage);
  }

  get allTranscriptedParts() {
    const parts: (TimestampedAssemblyAITurnMessage[] | { ended: boolean })[] = [];
    if (this.transcriptSnapshots.length) {
      this.transcriptSnapshots.forEach(snapshot => {
        const sorted = Array.from(snapshot.transcriptMap.values()).sort(
          (a, b) => a.turn_order - b.turn_order
        );
        if (sorted.length || sorted.find(m => m.transcript.trim()))
          parts.push(sorted, { ended: false });
      });
    }
    if (this.transcriptMap.size > 0) {
      const sorted = Array.from(this.transcriptMap.values()).sort(
        (a, b) => a.turn_order - b.turn_order
      );
      if (sorted.length || sorted.find(m => m.transcript.trim())) parts.push(sorted);
    }
    return parts;
  }

  get transcript(): string {
    const all = this.allTranscriptedParts
      .map(part =>
        Array.isArray(part)
          ? part
              .map(m => m.transcript)
              .join(" ")
              .trim()
          : ""
      )
      .filter(Boolean);
    return all.join("").trim();
  }

  get isTranscriptionPaused(): boolean {
    return !this.realtimeActive;
  }
}
